import React, { useState } from "react"
import { Input, Upload, UploadProps, Breadcrumb, Modal } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import { UploadRequestOption as RcCustomRequestOptions } from "rc-upload/lib/interface"

import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCloud,
  faRefresh,
  faHome,
  faFolderPlus,
} from "@fortawesome/free-solid-svg-icons"

import { useUploadDocumentMutation } from "features/Documents/api/useDocumentsMutations"
import ProgressModal from "shared/ui/ProgressModal/ProgressModal.component"
import { calculateFileUploadDuration } from "utils/files/calculateFileUploadDuration"
import type { Project } from "stores/projectStore"

// Define the props interface
interface DocumentsHeaderProps {
  project: Project
  refresh: () => Promise<void> | void
  onSearch: (value: string) => void
  currentPath: string[]
  navigateToPath: (pathIndex: number) => void
  navigateToRoot: () => void
  onCreateFolder: (folderName: string) => Promise<void>
  isCreatingFolder: boolean
}

/**
 * DocumentsHeader component for uploading documents, searching, folder management, and navigation.
 *
 * @param props - The component props
 * @returns {JSX.Element} The rendered DocumentsHeader component
 */
const DocumentsHeader: React.FC<DocumentsHeaderProps> = ({
  project,
  refresh,
  onSearch,
  currentPath,
  navigateToPath,
  navigateToRoot,
  onCreateFolder,
  isCreatingFolder,
}) => {
  const { Search } = Input
  const uploadMutation = useUploadDocumentMutation()
  const isUploading = uploadMutation.isPending
  const [isLoadingModalVisible, setIsLoadingModalVisible] =
    useState<boolean>(false)
  const [modalDuration, setModalDuration] = useState<number>(10000) // Default duration
  const [isFolderModalVisible, setIsFolderModalVisible] =
    useState<boolean>(false)
  const [newFolderName, setNewFolderName] = useState<string>("")

  /**
   * Custom request handler for file upload
   */
  const customRequest = (options: RcCustomRequestOptions): void => {
    const { file, onSuccess, onError } = options

    const uploadFile = async (): Promise<void> => {
      try {
        // Type guard to ensure file is not a string
        if (typeof file === "string") {
          throw new Error("File upload from string path is not supported")
        }

        // Extract filename - RcFile and File have name property, Blob doesn't
        const filename = "name" in file ? file.name : "unnamed-file"

        // Get current folder path for upload
        const currentFolder = currentPath.join("/")

        const response = await uploadMutation.mutateAsync({
          projectId: project.id,
          owner: project.Created_by,
          filename,
          file,
          folder: currentFolder, // Upload to current folder
          overwrite: true, // Overwrite existing files
        })
        if (onSuccess) {
          onSuccess(response)
        }
      } catch (error) {
        if (onError) {
          onError(error as Error)
        }
      }
    }

    uploadFile()
  }

  /**
   * Handles upload status changes
   */
  const handleChange: UploadProps["onChange"] = (info): void => {
    if (info.file.status === "uploading") {
      // Dynamically set the modal duration based on the original file size (before compression)
      const dynamicDuration = calculateFileUploadDuration(info.file.size || 0)
      setModalDuration(dynamicDuration)
      setIsLoadingModalVisible(true)
    }
    if (info.file.status === "done") {
      setIsLoadingModalVisible(false)
      notify.toast.success(`${info.file.name} file uploaded successfully`)
      refresh()
    } else if (info.file.status === "error") {
      setIsLoadingModalVisible(false)
      notify.toast.error(`${info.file.name} file upload failed.`)
    }
  }

  const uploadProps: UploadProps = {
    name: "file",
    customRequest,
    onChange: handleChange,
    showUploadList: false,
  }

  /**
   * Handles folder creation
   */
  const handleCreateFolder = async (): Promise<void> => {
    if (!newFolderName.trim()) {
      notify.toast.error("Please enter a folder name")
      return
    }

    try {
      await onCreateFolder(newFolderName.trim())
      setIsFolderModalVisible(false)
      setNewFolderName("")
      notify.toast.success(`Folder "${newFolderName}" created successfully`)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred"
      notify.toast.error(`Failed to create folder: ${errorMessage}`)
    }
  }

  /**
   * Creates breadcrumb items for navigation
   */
  const breadcrumbItems = [
    {
      title: (
        <span
          onClick={navigateToRoot}
          className="cursor-pointer hover:text-[#36c3ed] flex items-center"
        >
          <FontAwesomeIcon icon={faHome} className="mr-1" />
          Documents
        </span>
      ),
    },
    ...currentPath.map((pathPart, index) => ({
      title: (
        <span
          onClick={() => navigateToPath(index)}
          className="cursor-pointer hover:text-[#36c3ed]"
        >
          {pathPart}
        </span>
      ),
    })),
  ]

  return (
    <>
      <ProgressModal
        title={"Uploading File..."}
        isLoading={isLoadingModalVisible}
        duration={modalDuration} // Set the modal duration dynamically
      />

      {/* Folder Creation Modal */}
      <Modal
        title="Create New Folder"
        open={isFolderModalVisible}
        footer={null}
        confirmLoading={isCreatingFolder}
        closeIcon={null}
      >
        <Input
          placeholder="Enter folder name"
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          onPressEnter={handleCreateFolder}
          autoFocus
        />
        <div className="flex flex-row space-x-2 mt-5">
          <DataQButton
            className="ml-auto"
            type="secondary"
            onClick={() => {
              setIsFolderModalVisible(false)
              setNewFolderName("")
            }}
          >
            Cancel
          </DataQButton>
          <DataQButton
            type="primary"
            onClick={handleCreateFolder}
            isDisabled={!newFolderName.trim() || isCreatingFolder}
          >
            Create
          </DataQButton>
        </div>
      </Modal>

      <div className="flex flex-col w-full p-5 space-y-3">
        {/* Breadcrumb Navigation */}
        {currentPath.length > 0 && (
          <div className="flex items-center">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        )}

        {/* Search and Action Buttons */}
        <div className="flex flex-row w-full">
          <Search
            placeholder="Search by file name"
            allowClear
            onSearch={onSearch}
            size="large"
            className="my-auto"
            style={{
              width: 300,
            }}
          />

          <DataQButton
            type="primary"
            isLoading={isUploading}
            className="ml-auto"
            onClick={refresh}
          >
            Refresh <FontAwesomeIcon icon={faRefresh} className="ml-2" />
          </DataQButton>

          <DataQButton
            type="secondary"
            isLoading={isCreatingFolder}
            className="ml-2"
            onClick={() => setIsFolderModalVisible(true)}
          >
            New Folder <FontAwesomeIcon icon={faFolderPlus} className="ml-2" />
          </DataQButton>

          <Upload {...uploadProps} className="ml-2">
            <DataQButton type="primary" isLoading={isUploading}>
              Upload Documents
              <FontAwesomeIcon icon={faCloud} className="ml-2" />
            </DataQButton>
          </Upload>
        </div>
      </div>
    </>
  )
}

export default DocumentsHeader
